// Localização: frontend/src/pages/Escalas/SugestaoEscala.jsx
// Componente para exibir e gerenciar sugestão de escala gerada pela IA

import React, { useState } from 'react';
import moment from 'moment';
import { 
    CheckCircle, 
    XCircle, 
    AlertTriangle, 
    Users, 
    Clock, 
    TrendingUp,
    Sparkles,
    User,
    Calendar,
    Wrench,
    Download,
    FileText
} from 'lucide-react';
import PrimaryButton from '../../components/Common/PrimaryButton';
import SecondaryButton from '../../components/Common/SecondaryButton';
import { aprovarSugestao, rejeitarSugestao } from '../../services/escalaService';
import { exportarEscala } from '../../utils/escalaExportUtils';
import { useAuth } from '../../context/AuthContext';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import EstatisticasDetalhadas from '../../components/Escalas/EstatisticasDetalhadas';

const SugestaoEscala = ({ sugestao, onAprovar, onRejeitar, onClose }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showRejeitarModal, setShowRejeitarModal] = useState(false);
    const [feedbackRejeicao, setFeedbackRejeicao] = useState('');
    const [alertMessage, setAlertMessage] = useState(null);
    const [showConfirmarConflitosModal, setShowConfirmarConflitosModal] = useState(false);
    const [exportando, setExportando] = useState(false);

    if (!sugestao || !sugestao.sugestao) {
        return (
            <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                <p className="text-gray-600">Sugestão não disponível</p>
            </div>
        );
    }

    const { sugestao: dadosSugestao, conflitos = [], violacoes_carga_horaria = [], estatisticas } = sugestao;
    const { periodo, itens = [], observacoes = [] } = dadosSugestao;

    const handleAprovar = async (aceitarConflitos = false) => {
        // Se há conflitos e o usuário não aceitou explicitamente, mostrar modal de confirmação
        if (conflitos.length > 0 && !aceitarConflitos) {
            setShowConfirmarConflitosModal(true);
            return;
        }

        setLoading(true);
        setAlertMessage(null);

        try {
            await aprovarSugestao(sugestao.id, null);
            onAprovar();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao aprovar sugestão';
            setAlertMessage({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const handleSolucionarConflitos = () => {
        setShowConfirmarConflitosModal(true);
    };

    const handleRejeitar = async () => {
        if (!feedbackRejeicao.trim()) {
            setAlertMessage({ 
                type: 'error', 
                message: 'Por favor, forneça um feedback sobre a rejeição' 
            });
            return;
        }

        setLoading(true);
        setAlertMessage(null);

        try {
            await rejeitarSugestao(sugestao.id, feedbackRejeicao);
            onRejeitar();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao rejeitar sugestão';
            setAlertMessage({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    // Agrupar itens por data e ordenar por horário
    const itensPorData = itens.reduce((acc, item) => {
        const data = item.data_plantao;
        if (!acc[data]) {
            acc[data] = [];
        }
        acc[data].push(item);
        return acc;
    }, {});

    // Ordenar itens dentro de cada data por horário de início
    Object.keys(itensPorData).forEach(data => {
        itensPorData[data].sort((a, b) => {
            const horaA = moment(`${a.data_plantao} ${a.hora_inicio}`, 'YYYY-MM-DD HH:mm:ss');
            const horaB = moment(`${b.data_plantao} ${b.hora_inicio}`, 'YYYY-MM-DD HH:mm:ss');
            return horaA.diff(horaB);
        });
    });

    // Ordenar as datas
    const datasOrdenadas = Object.keys(itensPorData).sort((a, b) => {
        return moment(a).diff(moment(b));
    });

    const getTipoPlantaoColor = (tipo) => {
        const colors = {
            'diurno': 'bg-blue-100 text-blue-800',
            'noturno': 'bg-purple-100 text-purple-800',
            'integral': 'bg-green-100 text-green-800',
            'madrugada': 'bg-indigo-100 text-indigo-800'
        };
        return colors[tipo] || 'bg-gray-100 text-gray-800';
    };

    const formatScore = (score) => {
        return (score * 100).toFixed(0);
    };

    return (
        <div className="space-y-6">
            {/* Alertas */}
            {alertMessage && (
                <Alert
                    type={alertMessage.type}
                    message={alertMessage.message}
                    onClose={() => setAlertMessage(null)}
                />
            )}

            {/* Estatísticas Detalhadas */}
            <EstatisticasDetalhadas
                itens={itens}
                estatisticas={estatisticas}
                periodo={periodo}
            />

            {/* Alertas de Conflitos */}
            {conflitos.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <h4 className="font-semibold text-red-900">Conflitos Detectados</h4>
                    </div>
                    <ul className="space-y-1 text-sm text-red-800">
                        {conflitos.map((conflito, index) => (
                            <li key={index}>• {conflito.mensagem}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Alertas de Violações de Carga Horária */}
            {violacoes_carga_horaria.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-2">
                        <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <h4 className="font-semibold text-yellow-900">Violações de Carga Horária</h4>
                    </div>
                    <ul className="space-y-1 text-sm text-yellow-800">
                        {violacoes_carga_horaria.map((violacao, index) => (
                            <li key={index}>• {violacao.mensagem}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Observações */}
            {observacoes.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Observações</h4>
                    <ul className="space-y-1 text-sm text-blue-800">
                        {observacoes.map((obs, index) => (
                            <li key={index}>• {obs}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Itens da Escala */}
            <div>
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Itens da Escala ({itens.length} plantões)
                </h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                    {datasOrdenadas.map((data) => {
                        const itensData = itensPorData[data];
                        return (
                        <div key={data} className="bg-gray-50 rounded-lg p-4">
                            <div className="font-semibold text-gray-900 mb-3">
                                {moment(data).format('dddd, DD [de] MMMM [de] YYYY')}
                            </div>
                            <div className="space-y-2">
                                {itensData.map((item, index) => (
                                    <div
                                        key={index}
                                        className="bg-white rounded-lg p-3 border border-gray-200"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <User className="w-4 h-4 text-gray-500" />
                                                    <span className="font-medium text-gray-900">
                                                        {item.usuario_nome}
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTipoPlantaoColor(item.tipo_plantao)}`}>
                                                        {item.tipo_plantao}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4 text-sm text-gray-600 ml-6">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        <span>
                                                            {item.hora_inicio.substring(0, 5)} - {item.hora_fim.substring(0, 5)}
                                                        </span>
                                                    </div>
                                                    {item.paciente_nome && (
                                                        <div className="flex items-center gap-1">
                                                            <Users className="w-3 h-3" />
                                                            <span>{item.paciente_nome}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {item.justificativa && (
                                                    <div className="text-xs text-gray-500 mt-1 ml-6 italic">
                                                        {item.justificativa}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal de Rejeição */}
            {showRejeitarModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Rejeitar Sugestão
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Por favor, forneça um feedback sobre por que esta sugestão está sendo rejeitada. 
                            Isso nos ajudará a melhorar as sugestões futuras.
                        </p>
                        <textarea
                            value={feedbackRejeicao}
                            onChange={(e) => setFeedbackRejeicao(e.target.value)}
                            placeholder="Ex: Distribuição não está equilibrada, conflitos com agendamentos existentes..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
                            rows={4}
                        />
                        <div className="flex justify-end gap-2">
                            <SecondaryButton
                                onClick={() => {
                                    setShowRejeitarModal(false);
                                    setFeedbackRejeicao('');
                                }}
                            >
                                Cancelar
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={handleRejeitar}
                                disabled={loading || !feedbackRejeicao.trim()}
                                className="bg-red-600 hover:bg-red-700"
                            >
                                {loading ? <LoadingIndicator small /> : 'Rejeitar'}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação - Aprovar com Conflitos */}
            {showConfirmarConflitosModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Aprovar com Conflitos
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">
                                    Esta sugestão contém {conflitos.length} conflito(s) detectado(s). 
                                    Ao aprovar, os espaços com conflitos ficarão abertos para preenchimento manual posterior.
                                </p>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                                    <p className="text-xs text-yellow-800">
                                        <strong>Observação:</strong> Você poderá preencher manualmente os períodos 
                                        com conflitos após a aprovação da escala.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <SecondaryButton
                                onClick={() => setShowConfirmarConflitosModal(false)}
                                disabled={loading}
                            >
                                Cancelar
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={() => {
                                    setShowConfirmarConflitosModal(false);
                                    handleAprovar(true);
                                }}
                                disabled={loading}
                                className="bg-yellow-600 hover:bg-yellow-700"
                            >
                                {loading ? (
                                    <>
                                        <LoadingIndicator small />
                                        Aprovando...
                                    </>
                                ) : (
                                    <>
                                        <Wrench className="w-4 h-4 mr-2" />
                                        Aprovar e Solucionar Depois
                                    </>
                                )}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Ações */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
                <div className="relative group">
                    <SecondaryButton
                        onClick={() => {}}
                        disabled={loading || exportando}
                        className="flex items-center gap-2"
                    >
                        {exportando ? (
                            <>
                                <LoadingIndicator small />
                                Exportando...
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                Exportar
                            </>
                        )}
                    </SecondaryButton>
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                        <button
                            onClick={async () => {
                                setExportando(true);
                                try {
                                    await exportarEscala(dadosSugestao, 'pdf', user?.organizacao_nome || '');
                                    setAlertMessage({ type: 'success', message: 'Escala exportada em PDF!' });
                                } catch (error) {
                                    console.error('Erro ao exportar PDF:', error);
                                    setAlertMessage({ type: 'error', message: 'Erro ao exportar PDF.' });
                                } finally {
                                    setExportando(false);
                                }
                            }}
                            disabled={exportando}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg disabled:opacity-50"
                        >
                            <FileText className="w-4 h-4" />
                            Exportar PDF
                        </button>
                        <button
                            onClick={async () => {
                                setExportando(true);
                                try {
                                    await exportarEscala(dadosSugestao, 'csv', user?.organizacao_nome || '');
                                    setAlertMessage({ type: 'success', message: 'Escala exportada em CSV!' });
                                } catch (error) {
                                    console.error('Erro ao exportar CSV:', error);
                                    setAlertMessage({ type: 'error', message: 'Erro ao exportar CSV.' });
                                } finally {
                                    setExportando(false);
                                }
                            }}
                            disabled={exportando}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-b-lg disabled:opacity-50"
                        >
                            <FileText className="w-4 h-4" />
                            Exportar Excel (CSV)
                        </button>
                    </div>
                </div>
                <SecondaryButton
                    onClick={onClose}
                    disabled={loading}
                >
                    Fechar
                </SecondaryButton>
                <SecondaryButton
                    onClick={() => setShowRejeitarModal(true)}
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700 text-white"
                >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeitar
                </SecondaryButton>
                {conflitos.length > 0 && (
                    <PrimaryButton
                        onClick={handleSolucionarConflitos}
                        disabled={loading}
                        className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700"
                    >
                        <Wrench className="w-4 h-4" />
                        Solucionar Conflitos
                    </PrimaryButton>
                )}
                <PrimaryButton
                    onClick={() => handleAprovar(false)}
                    disabled={loading}
                    className="flex items-center gap-2"
                >
                    {loading ? (
                        <>
                            <LoadingIndicator small />
                            Aprovando...
                        </>
                    ) : (
                        <>
                            <CheckCircle className="w-4 h-4" />
                            Aprovar Sugestão
                        </>
                    )}
                </PrimaryButton>
            </div>

            {conflitos.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    Esta sugestão contém {conflitos.length} conflito(s). 
                    Você pode aprovar mesmo assim usando o botão "Solucionar Conflitos" para preencher manualmente depois.
                </div>
            )}
        </div>
    );
};

export default SugestaoEscala;

